
import Vue from "vue";
import { ViewCandidates } from "@/interfaces/recruiter/candidates/view_candidates/view_candidates";
import ViewCandidatesBody from "@/components/recruiter/candidates/view_candidates/ViewCandidatesBody.vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import {
  ADVANCE_SEARCH,
  ALL_CANDIDATES,
  FETCH_MATCHING_CANDIDATES,
  GET_FAVORITE_CANDIDATES,
  JOBS_POSTED_BY_RECRUITER,
  MATCHING_CANDIDATES,
  SEARCH_TEXT,
  TOP_JOBS_WITH_CANDIDATES,
  VIEW_CANDIDATES_DYNAMICALLY,
  VIEW_CANDIDATES_PAGINATION
} from "@/store/modules/recruiter/constants";
import { ROOT_ERROR } from "@/store/modules/root/constants";
import {
  SearchCandidatesPayload,
  SearchedCandidate,
  ViewCandidatesPagination
} from "@/store/modules/recruiter/interfaces";
// COMMENTED OUT SEARCH COMPONENT IMPORTS
// import AutoCompleteJobSearch from "@/components/shared/jobs/AutoCompleteJobSearch.vue";
// import AutoCompleteUserSearch from "@/components/shared/AutoCompleteUserSearch.vue";
// import AutoCompleteSkillSearch from "@/components/shared/jobs/AutoCompleteSkillSearch.vue";
import { SearchJobObject } from "@/interfaces/shared/jobs/auto_complete_job_search";
import { SearchUserObject } from "@/interfaces/shared/auto_complete_user_search";
import { Candidates } from "@/interfaces/data_objects/candidates";
import { GetFavoriteCandidates } from "@/interfaces/responses/favorite_candidates";
import { SiteDirections } from "@/store/modules/common/interfaces";
import { SITE_DIRECTION, FILTER_DRAWER_OPEN } from "@/store/modules/common/constants";
// import SearchAnything from "@/components/recruiter/dashboard/SearchAnything.vue";
import UploadCVsModal from "@/components/recruiter/candidates/UploadCVsModal.vue";
import { get_candidate_pools, get_search_candidates, get_pool_deletion_preview, delete_candidate_pool } from "@/store/apis/recruiter";

export default Vue.extend({
  /**
   * Activated this page
   *  1: User click on view more button (matching candidates) from dashboard
   *  2: User click on count number from job listing page
   *  3: User click on candidate tab from sidebar
   */
  name: "ViewCandidates",
  components: {
    ViewCandidatesBody,
    // COMMENTED OUT SEARCH COMPONENTS
    // AutoCompleteSkillSearch,
    // AutoCompleteJobSearch,
    // AutoCompleteUserSearch,
    // SearchAnything,
    UploadCVsModal
  },
  data(): ViewCandidates & {
    searched_user_ids: number[];
    showUploadModal: boolean;
    candidatePools: any[];
    loadingPools: boolean;
    selectedPool: any;
    showingPoolView: boolean;
    poolTotalCandidates: number;
    poolPagination: number;
    showDeleteDialog: boolean;
    poolToDelete: any;
    deletingPool: boolean;
    experienceLevelFilter: string;
    yearsOfExperienceFilter: number | null;
    skillsLevelFilter: number | null;
    englishProficiencyFilter: number | null;
    arabicProficiencyFilter: number | null;
  } {
    return {
      loading: true,
      dynamic_data: false,
      active_tab: "internal",
      matching_candidates: [],
      job_with_candidate: null,
      advance_search_text: null,
      search_job_id: null,
      job_id: null,
      pagination: 1,
      user_ids: [],
      searched_user_ids: [],
      showUploadModal: false,
      candidatePools: [],
      loadingPools: false,
      selectedPool: null,
      showingPoolView: false,
      poolTotalCandidates: 0,
      poolPagination: 1,
      showDeleteDialog: false,
      poolToDelete: null,
      deletingPool: false,
      experienceLevelFilter: '',
      yearsOfExperienceFilter: null,
      skillsLevelFilter: null,
      englishProficiencyFilter: null,
      arabicProficiencyFilter: null
    };
  },
  computed: {
    SiteDirections() {
      return SiteDirections;
    },
    ...mapGetters("common", {
      get_site_direction: SITE_DIRECTION,
      filter_drawer_open: FILTER_DRAWER_OPEN
    }),
    ...mapGetters("recruiter", {
      view_candidates_dynamically: VIEW_CANDIDATES_DYNAMICALLY,
      get_matching_candidates: FETCH_MATCHING_CANDIDATES,
      top_posted_jobs: TOP_JOBS_WITH_CANDIDATES,
      get_all_candidates: ALL_CANDIDATES,
      view_candidates_pagination: VIEW_CANDIDATES_PAGINATION,
      get_search_text: SEARCH_TEXT
    })
  },
  watch: {
    active_tab(newTab) {
      // When switching back to internal tab, restore pool view if we were viewing a pool
      if (newTab === 'internal' && this.selectedPool && this.showingPoolView) {
        // Reload the pool candidates to maintain the view
        this.loadPoolCandidates(this.selectedPool, this.poolPagination);
      }
    },
    $route() {
      // Force close filter drawer when route changes
      this.set_filter_drawer_open(false);
    }
  },
  async mounted() {
    // Force close filter drawer on mount to fix stuck state
    this.set_filter_drawer_open(false);
    
    // Load candidate pools for internal tab
    await this.loadCandidatePools();
    
    // Listen for global filter events
    this.$root.$on('apply-global-filters', this.handleGlobalFilters);
    this.$root.$on('clear-global-filters', this.handleClearGlobalFilters);
    
    const ids = sessionStorage.getItem("advance_search_user");
    if (!this.get_search_text) {
      this.searched_user_ids = [];
    }
    if (ids) {
      const array = ids.split(",").map((item: string) => parseInt(item));
      await this.fetch_advance_search_candidates(array);
      sessionStorage.removeItem("advance_search_user");
    } else {
      if (this.view_candidates_dynamically.active)
        await this.init_dynamically_data();
      else {
        await this.init_default_way_data();
      }
    }
    this.loading = false;
  },
  methods: {
    ...mapMutations("common", {
      set_filter_drawer_open: FILTER_DRAWER_OPEN
    }),
    ...mapMutations({
      root_error: ROOT_ERROR
    }),
    ...mapActions("recruiter", {
      fetch_matching_candidates: FETCH_MATCHING_CANDIDATES,
      fetch_top_jobs_with_candidates: JOBS_POSTED_BY_RECRUITER,
      fetch_matching_candidates_by_id: MATCHING_CANDIDATES,
      fetch_favorite_candidates_by_job: GET_FAVORITE_CANDIDATES,
      advance_search: ADVANCE_SEARCH
    }),
    ...mapMutations("recruiter", {
      set_candidates_view_dynamically: VIEW_CANDIDATES_DYNAMICALLY,
      set_all_candidates: ALL_CANDIDATES,
      set_view_candidates_pagination: VIEW_CANDIDATES_PAGINATION,
      set_search_text: SEARCH_TEXT
    }),
    transform_filter_icon(icon: HTMLElement): HTMLElement {
      const darkColor = "#4A5568";
      for (let node of icon.children) {
        node.setAttribute("fill", darkColor);
        node.setAttribute("stroke", darkColor);
      }
      return icon;
    },
    update_candidates(tab: string) {
      switch (tab) {
        case "internal":
          this.active_tab = "internal";
          break;
        case "external":
          this.active_tab = "external";
          break;
      }
      if (this.job_with_candidate) {
        if (this.active_tab === "internal") {
          // Show all profiles for internal (like online was working before)
          this.matching_candidates = this.job_with_candidate.matching_candidates;
        } else {
          // Filter by availability for external
          this.matching_candidates =
            this.job_with_candidate.matching_candidates.filter(
              (candidate: SearchedCandidate) =>
                candidate.availability.includes(this.active_tab)
            );
        }
      } else {
        if (this.active_tab === "internal") {
          // Show all profiles for internal (like online was working before)
          this.matching_candidates = this.get_all_candidates.results;
        } else {
          // Filter by availability for external
          this.matching_candidates = this.filter_candidates_by_availability(
            this.get_all_candidates.results
          );
        }
      }
    },
    async init_dynamically_data() {
      this.search_job_id = this.view_candidates_dynamically.job_id;
      this.user_ids = this.view_candidates_dynamically.user_ids || [];
      await this.fetch_candidates_with_pagination(
        this.view_candidates_pagination.pagination
      );
    },
    async init_default_way_data() {
      await this.fetch_candidates_with_pagination(
        this.view_candidates_pagination.pagination
      );
    },
    /**
     * Function to processing search skill filter
     */
    async searched_skill(value: string | null | undefined) {
      // If skill search filter is set & value isn't set yet => return
      if (
        this.view_candidates_pagination.searched_skill_filter &&
        value === undefined
      )
        return;
      // Update view candidate pagination payload
      this.set_view_candidates_pagination({
        pagination: this.view_candidates_pagination.pagination,
        searched_skill_filter: value ?? ""
      });
      // Update data set in order to view candidates
      this.set_candidates_view_dynamically({
        active: !!(
          value ||
          this.view_candidates_dynamically.job_id ||
          this.view_candidates_dynamically.user_ids?.length
        ),
        loading: false,
        job_id: this.view_candidates_dynamically.job_id,
        job_title: this.view_candidates_dynamically.job_title,
        skill: value ? value : "",
        user_ids: this.view_candidates_dynamically.user_ids || []
      });
      // If skill search filter reset => fetch all candidates (Optionally based on job if set)
      if (!value) {
        this.set_view_candidates_pagination({
          pagination: 1,
          searched_skill_filter:
            this.view_candidates_pagination.searched_skill_filter
        });
        await this.fetch_candidates_with_pagination(1);
      }
    },
    /**
     * Function to processing search job filter
     */
    async searched_job(value: SearchJobObject) {
      // Update local variable => hold the job id
      this.search_job_id = value ? value.job_id : null;
      // Update data set in order to view candidates
      this.set_candidates_view_dynamically({
        active: !!(
          value ||
          this.view_candidates_pagination.searched_skill_filter ||
          this.view_candidates_dynamically.user_ids?.length
        ),
        loading: false,
        job_id: value ? value.job_id : null,
        job_title: value ? value.text : "",
        skill: this.view_candidates_pagination.searched_skill_filter,
        user_ids: this.view_candidates_dynamically.user_ids || []
      });
      if (!value) {
        this.set_view_candidates_pagination({
          pagination: 1,
          searched_skill_filter:
            this.view_candidates_pagination.searched_skill_filter
        });
        await this.fetch_candidates_with_pagination(1);
      }
    },
    /**
     * Function to extract Ids from selected user
     */
    async searched_user(value: SearchUserObject[]) {
      this.user_ids = value?.map((item: SearchUserObject) => item.id);
      this.searched_user_ids = [];
      this.set_search_text("");
      // Update data set in order to view candidates
      this.set_candidates_view_dynamically({
        active: !!(
          value?.length ||
          this.view_candidates_pagination.searched_skill_filter ||
          this.view_candidates_dynamically.job_id
        ),
        loading: false,
        job_id: this.view_candidates_dynamically.job_id,
        job_title: this.view_candidates_dynamically.job_title,
        skill: this.view_candidates_pagination.searched_skill_filter,
        user_ids: this.user_ids
      });
      if (this.user_ids?.length === 0) {
        this.searched_user_ids = [];
        this.set_view_candidates_pagination({
          pagination: 1,
          searched_skill_filter:
            this.view_candidates_pagination.searched_skill_filter
        });
        await this.fetch_candidates_with_pagination(1);
      }
    },
    /**
     * FUnction to fetch top posted job data
     * For by default displaying in job autocomplete field
     */
    fetch_top_job_data(): SearchJobObject | null {
      if (this.top_posted_jobs.results?.length <= 0) return null;
      else if (
        this.view_candidates_dynamically.active &&
        this.view_candidates_dynamically.job_id
      ) {
        return {
          job_id: this.view_candidates_dynamically.job_id,
          text: this.view_candidates_dynamically.job_title
        };
      } else {
        return null;
      }
    },
    reset_data() {
      this.active_tab = "internal";
      this.matching_candidates = [];
      this.advance_search_text = null;
      this.search_job_id = null;
      this.job_with_candidate = null;
      this.loading = false;
      this.pagination = 1;
      this.job_id = null;
      this.user_ids = [];
      this.searched_user_ids = [];
      this.set_search_text("");
    },
    /**
     * Function to handle filter form submit
     */
    async filter_form_submit() {
      this.loading = true;
      if (
        !this.advance_search_text &&
        !this.search_job_id &&
        !this.view_candidates_pagination.searched_skill_filter &&
        !this.user_ids?.length &&
        !this.get_search_text
      )
        return;
      if (this.get_search_text) {
        const response = await this.advance_search(this.get_search_text);
        if (response && response.data?.length) {
          this.user_ids = response.data;
          sessionStorage.setItem("advance_search_user", response.data);
        } else {
          this.user_ids = [];
          this.set_search_text("");
          this.root_error(response?.msg);
        }
      }
      this.set_view_candidates_pagination({
        pagination: 1,
        searched_skill_filter:
          this.view_candidates_pagination.searched_skill_filter
      });
      await this.fetch_candidates_with_pagination();
      this.loading = false;
    },
    async process_favorite_candidates(
      data: SearchedCandidate[],
      job_id: number
    ) {
      // Fetching favorite candidates against job
      const favorites = await this.fetch_favorite_candidates_by_job(job_id);
      const res = data.map((obj: SearchedCandidate) => ({
        ...obj,
        is_favorite: false
      }));
      // If favorite candidates found => process true if found
      if (favorites?.length > 0) {
        return res.map((obj1: SearchedCandidate) => {
          let obj2 = favorites.find(
            (obj2: GetFavoriteCandidates) =>
              obj1.candidate_id === obj2.candidate_id
          );
          if (obj2) {
            obj1.is_favorite = true;
          }
          return obj1;
        });
      } else return res;
    },
    /**
     * Function to return filtered candidates based on availability(internal,external)
     * @param candidates
     */
    filter_candidates_by_availability(
      candidates: SearchedCandidate[]
    ): SearchedCandidate[] {
      if (this.active_tab === "internal") {
        // Show all profiles for internal (like online was working before)
        return candidates;
      } else {
        // Filter by availability for external
        return candidates.filter((candidate: SearchedCandidate) =>
          candidate.availability.includes(this.active_tab)
        );
      }
    },
    /**
     * Function to fetch candidates with pagination
     */
    async fetch_candidates_with_pagination(page = 1, limit = 12) {
      this.loading = true;
      const payload: SearchCandidatesPayload = {
        page: page - 1,
        limit_per_page: limit,
        n: 20,
        user_ids:
          this.searched_user_ids?.length > 0
            ? this.searched_user_ids
            : this.user_ids?.length > 0
            ? this.user_ids
            : []
      };
      // If job id is selected in filter => set else set null
      if (this.search_job_id) {
        payload.job_ids = [this.search_job_id];
        this.job_id = this.search_job_id;
      } else {
        this.job_id = null;
        this.job_with_candidate = null;
      }
      // If skill search filter is set => set skill name
      if (this.view_candidates_pagination.searched_skill_filter) {
        payload.skill_name =
          this.view_candidates_pagination.searched_skill_filter;
      }
      
      // Add candidate name filter
      if (this.advance_search_text) {
        (payload as any).candidate_name = this.advance_search_text;
      }
      
      // Always add pool name (if we're in pool view)
      if (this.selectedPool && this.showingPoolView) {
        (payload as any).pool_name = this.selectedPool.pool_name;
      }
      
      // Add new filter parameters
      if (this.experienceLevelFilter) {
        (payload as any).experience_level = this.experienceLevelFilter;
      }
      
      if (this.yearsOfExperienceFilter) {
        (payload as any).years_of_experience = this.yearsOfExperienceFilter;
      }
      
      if (this.skillsLevelFilter) {
        (payload as any).skills_level = this.skillsLevelFilter;
      }
      
      if (this.englishProficiencyFilter) {
        (payload as any).english_proficiency = this.englishProficiencyFilter;
      }
      
      if (this.arabicProficiencyFilter) {
        (payload as any).arabic_proficiency = this.arabicProficiencyFilter;
      }
      
      console.log('📤 API Payload with filters:', payload);
      // Fetching all candidates
      const _all_candidates = await this.fetch_matching_candidates(payload);
      let all_candidates = _all_candidates;
      if (this.job_id) {
        // Set candidates whose score is greater than 20%
        all_candidates = _all_candidates.results.filter(
          (candidate: Candidates.Candidates) => candidate.score > 0.2
        );
      }
      // If candidates found
      if (
        (all_candidates &&
          all_candidates.results &&
          all_candidates.results?.length > 0) ||
        all_candidates?.length > 0
      ) {
        // Set filter count to -1 => if not set already
        if (!all_candidates.filtered_count) all_candidates.filtered_count = -1;
        this.set_all_candidates(all_candidates); // Setting in store
        // If job filter is applied
        if (this.job_id) {
          // Fetching favorite candidates
          const res = await this.process_favorite_candidates(
            this.job_id
              ? this.get_all_candidates
              : this.get_all_candidates.results,
            this.job_id
          );
          // Use to hold all candidates when job filter is applied
          this.job_with_candidate = {
            candidates_loading: false,
            matching_candidates: res
          };
          // Fetching matching candidates with favorites
          this.matching_candidates = this.filter_candidates_by_availability(
            this.job_with_candidate.matching_candidates
          );
        }
        // If job filter not applied
        else {
          this.matching_candidates = this.filter_candidates_by_availability(
            this.get_all_candidates.results
          );
        }
      }
      // If fetching all candidates failed
      else {
        this.root_error("No Candidates Found");
        this.set_all_candidates({ total: 0, results: [], filtered_count: -1 });
        this.reset_data();
        // COMMENTED OUT - Job filter reset (component removed)
        // if (this.search_job_id) {
        //   const ref = this.$refs.autocomplete_job as InstanceType<
        //     typeof AutoCompleteJobSearch
        //   >;
        //   ref.reset_state();
        //   this.search_job_id = null;
        //   this.job_id = null;
        // }
        // COMMENTED OUT - Skill filter reset (component removed)
        // if (this.view_candidates_pagination.searched_skill_filter) {
        //   const ref = this.$refs.autocomplete_skill as InstanceType<
        //     typeof AutoCompleteSkillSearch
        //   >;
        //   ref.reset_state();
        //   this.set_view_candidates_pagination({
        //     pagination: this.view_candidates_pagination.pagination,
        //     searched_skill_filter: ""
        //   });
        // }
        // COMMENTED OUT - User filter reset (component removed)
        // if (this.user_ids?.length) {
        //   const ref = this.$refs.autocomplete_user as InstanceType<
        //     typeof AutoCompleteUserSearch
        //   >;
        //   ref.reset_state();
        // }
        // If dynamic view set
        if (this.view_candidates_dynamically.active) {
          this.set_candidates_view_dynamically({
            active: false,
            loading: false,
            job_id: null,
            job_title: "",
            skill: "",
            user_ids: []
          });
        }
      }
      this.loading = false;
    },
    async fetch_advance_search_candidates(ids: number[]) {
      this.set_candidates_view_dynamically({
        active: true,
        loading: false,
        job_id: null,
        job_title: "",
        skill: "",
        user_ids: ids
      });
      this.user_ids = ids?.length > 0 ? ids : [];
      this.searched_user_ids = ids;
      await this.fetch_candidates_with_pagination(1);
    },
    upload_cvs() {
      this.showUploadModal = true;
    },
    async loadCandidatePools() {
      this.loadingPools = true;
      try {
        const response = await get_candidate_pools();
        
        if (response.data && response.data.statusCode === 200 && response.data.data) {
          this.candidatePools = response.data.data;
          console.log('✅ Pools loaded:', this.candidatePools);
        } else {
          this.candidatePools = [];
          console.warn('❌ Failed to load pools:', response.data?.message);
        }
      } catch (error) {
        console.error('💥 Error loading pools:', error);
        this.candidatePools = [];
      } finally {
        this.loadingPools = false;
      }
    },
    selectPool(pool: any) {
      this.selectedPool = pool;
      this.showingPoolView = true;
      this.poolPagination = 1; // Reset to first page
      // Filter candidates by pool
      this.loadPoolCandidates(pool, 1);
    },
    async fetchPoolCandidatesPage(page: number) {
      if (this.selectedPool) {
        await this.loadPoolCandidates(this.selectedPool, page);
      }
    },
    async loadPoolCandidates(pool: any, page = 1) {
      this.loading = true;
      try {
        // Use the search API with pool_name parameter
        const searchPayload: any = {
          job_ids: this.job_id ? [this.job_id] : [],
          pool_name: pool.pool_name,
          page: page - 1, // Convert to 0-based index for API
          limit_per_page: 20 // Load more candidates for pool view
        };
        
        // Update current page
        this.poolPagination = page;
        
        // Add candidate name filter
        if (this.advance_search_text) {
          searchPayload.candidate_name = this.advance_search_text;
        }
        
        // Add new filter parameters
        if (this.experienceLevelFilter) {
          searchPayload.experience_level = this.experienceLevelFilter;
        }
        
        if (this.yearsOfExperienceFilter) {
          searchPayload.years_of_experience = this.yearsOfExperienceFilter;
        }
        
        if (this.skillsLevelFilter) {
          searchPayload.skills_level = this.skillsLevelFilter;
        }
        
        if (this.englishProficiencyFilter) {
          searchPayload.english_proficiency = this.englishProficiencyFilter;
        }
        
        if (this.arabicProficiencyFilter) {
          searchPayload.arabic_proficiency = this.arabicProficiencyFilter;
        }
        
        // Add skill filter if available
        if (this.view_candidates_pagination.searched_skill_filter) {
          searchPayload.skill_name = this.view_candidates_pagination.searched_skill_filter;
        }
        
        console.log('📤 Pool API Payload with filters:', searchPayload);
        
        // Use direct API call instead of Vuex action to get response
        const response = await get_search_candidates(searchPayload);

        if (response && response.data && response.data.data) {
          const responseData = response.data.data;
          const candidates = responseData.results || responseData;
          
          // Set candidates and total count
          this.matching_candidates = candidates;
          this.poolTotalCandidates = responseData.total || candidates.length;
          
          console.log('✅ Pool candidates loaded:', {
            poolName: pool.pool_name,
            totalFromAPI: responseData.total,
            candidatesCount: candidates.length,
            firstCandidate: candidates[0]
          });
          
          // Log first candidate for structure inspection
          if (candidates.length > 0) {
            console.log('🧑 First candidate structure:', candidates[0]);
          }
        } else {
          this.matching_candidates = [];
          this.poolTotalCandidates = 0;
          console.warn('❌ No candidates data in response for pool:', pool.pool_name);
          console.warn('❌ Response structure:', response.data);
        }
      } catch (error) {
        console.error('💥 Error loading pool candidates:', error);
        this.matching_candidates = [];
      } finally {
        this.loading = false;
      }
    },
    backToPoolsList() {
      this.showingPoolView = false;
      this.selectedPool = null;
      this.poolPagination = 1;
      this.poolTotalCandidates = 0;
      // Reset to show all candidates
      this.matching_candidates = this.filter_candidates_by_availability(
        this.get_all_candidates.results
      );
    },
    toggleGlobalFilterDrawer() {
      console.log('🔄 Toggling filter drawer. Current state:', this.filter_drawer_open);
      this.set_filter_drawer_open(!this.filter_drawer_open);
    },
    handleGlobalFilters(filters: any) {
      console.log('🔍 Applying global filters:', filters);
      
      // Apply candidate name filter
      if (filters.candidateName) {
        this.advance_search_text = filters.candidateName;
        this.set_search_text(filters.candidateName);
      } else {
        this.advance_search_text = '';
        this.set_search_text('');
      }
      
      // Apply experience level filter
      if (filters.experienceLevel) {
        // Store experience level for API calls
        this.experienceLevelFilter = filters.experienceLevel;
      } else {
        this.experienceLevelFilter = '';
      }
      
      // Apply years of experience filter
      if (filters.yearsOfExperience) {
        this.yearsOfExperienceFilter = filters.yearsOfExperience;
      } else {
        this.yearsOfExperienceFilter = null;
      }
      
      // Apply skills level filter
      if (filters.skillsLevel) {
        this.skillsLevelFilter = filters.skillsLevel;
      } else {
        this.skillsLevelFilter = null;
      }
      
      // Apply english proficiency filter
      if (filters.englishProficiency) {
        this.englishProficiencyFilter = filters.englishProficiency;
      } else {
        this.englishProficiencyFilter = null;
      }
      
      // Apply arabic proficiency filter
      if (filters.arabicProficiency) {
        this.arabicProficiencyFilter = filters.arabicProficiency;
      } else {
        this.arabicProficiencyFilter = null;
      }
      
      // Reset pagination and refresh candidates with new filters
      this.set_view_candidates_pagination({
        pagination: 1,
        searched_skill_filter: ''
      });
      
      // Refresh candidates with new filters
      if (this.showingPoolView && this.selectedPool) {
        // If we're in pool view, refresh pool candidates
        this.loadPoolCandidates(this.selectedPool, 1);
      } else {
        // Otherwise refresh regular candidates
        this.fetch_candidates_with_pagination(1);
      }
    },
    handleClearGlobalFilters() {
      console.log('🧹 Clearing global filters');
      
      // Clear all filter properties
      this.advance_search_text = '';
      this.set_search_text('');
      this.experienceLevelFilter = '';
      this.yearsOfExperienceFilter = null;
      this.skillsLevelFilter = null;
      this.englishProficiencyFilter = null;
      this.arabicProficiencyFilter = null;
      
      // Clear skill filter from pagination
      this.set_view_candidates_pagination({
        pagination: 1,
        searched_skill_filter: ''
      });
      
      // Reset to internal tab
      this.active_tab = 'internal';
      
      // Refresh candidates
      if (this.showingPoolView && this.selectedPool) {
        // If we're in pool view, refresh pool candidates
        this.loadPoolCandidates(this.selectedPool, 1);
      } else {
        // Otherwise refresh regular candidates
        this.fetch_candidates_with_pagination(1);
      }
    },
    formatDate(dateString: string) {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    confirmDeletePool(pool: any) {
      this.poolToDelete = pool;
      this.showDeleteDialog = true;
    },
    async deletePool() {
      if (!this.poolToDelete) return;
      
      this.deletingPool = true;
      try {
        const response = await delete_candidate_pool(this.poolToDelete.id.toString());
        
        if (response && response.data) {
          this.root_error(`Pool "${this.poolToDelete.pool_name}" deleted successfully`);
          
          // Remove pool from local list
          this.candidatePools = this.candidatePools.filter(p => p.id !== this.poolToDelete.id);
          
          // If we're currently viewing this pool, go back to pools list
          if (this.selectedPool?.id === this.poolToDelete.id) {
            this.backToPoolsList();
          }
          
          this.cancelDelete();
        }
      } catch (error) {
        console.error('💥 Error deleting pool:', error);
        this.root_error('Failed to delete pool. Please try again.');
      } finally {
        this.deletingPool = false;
      }
    },
    cancelDelete() {
      this.showDeleteDialog = false;
      this.poolToDelete = null;
      this.deletingPool = false;
    },
    async onUploadSuccess(message: string) {
      console.log('🎉 Upload success received in parent:', message);
      
      // Handle successful upload
      this.root_error(message); // Using root_error to show success message
      
      // Modal will close itself - we just need to refresh the data
      console.log('🔄 Refreshing pools list...');
      await this.loadCandidatePools();
      
      // If we're currently viewing a pool, refresh that pool's candidates
      if (this.showingPoolView && this.selectedPool) {
        console.log('🔄 Refreshing pool candidates for:', this.selectedPool.pool_name);
        await this.loadPoolCandidates(this.selectedPool, 1);
      } else {
        console.log('🔄 Refreshing regular candidates list...');
        // Otherwise refresh the regular candidates list
        await this.fetch_candidates_with_pagination(1);
      }
      
      console.log('✅ Data refresh completed');
    },
    onUploadError(message: string) {
      // Handle upload error
      this.root_error(message);
    },
    async search_anything(text: string) {
      this.set_search_text(text); // set the input text in store
      if (!text) {
        // if user clear the input
        this.set_candidates_view_dynamically({
          active: true,
          loading: false,
          job_id: null,
          job_title: "",
          skill: "",
          user_ids: []
        }); // init the data
        this.searched_user_ids = [];
        this.user_ids = [];
        const payload: ViewCandidatesPagination = {
          pagination: 1,
          searched_skill_filter: ""
        };
        sessionStorage.removeItem("advance_search_user");
        this.set_view_candidates_pagination(payload); // rest pagination
        this.fetch_candidates_with_pagination(); // init candidates
      }
    }
  },
  beforeDestroy() {
    // Clean up global filter event listeners
    this.$root.$off('apply-global-filters', this.handleGlobalFilters);
    this.$root.$off('clear-global-filters', this.handleClearGlobalFilters);
    
    if (this.$route.name !== "candidate-profile") {
      const payload: ViewCandidatesPagination = {
        pagination: 1,
        searched_skill_filter: ""
      };
      this.set_view_candidates_pagination(payload);
    }
  }
});
